/**
 * Lado direito do explorer: o caminho, as subpastas e os documentos da pasta aberta.
 * Pasta navegável e não liberada (D4) mostra o nome e as subpastas, e nenhum
 * documento nem descrição — o backend já não os manda.
 */
import type { ReactNode } from 'react'
import { Alert, Badge, Button, Group, Skeleton, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  IconEdit,
  IconFolder,
  IconFolderOff,
  IconFolderPlus,
  IconInfoCircle,
  IconLock,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react'
import { deleteKbFolder, getKbFolder, type KbDocument, type KbFolderNode } from '@/api/kb'
import { mensagemDeErro, type AcessoDaKb } from './acesso'
import { DocumentosDaPasta } from './DocumentosDaPasta'
import { estiloDoAlvo, juntarRefs, useAlvoDeArquivo, useAlvoDeArraste, useOrigemDeArraste } from './ArrastarESoltar'

export const SEM_ACESSO_AO_CONTEUDO = 'Você não tem acesso ao conteúdo desta pasta'

interface Props {
  pastaId: number
  acesso: AcessoDaKb
  documentos: KbDocument[]
  /** Pastas da mesma organização (destinos de "Mover") */
  pastasDaOrg: KbFolderNode[]
  onAbrir: (id: number) => void
  onNovaSubpasta: (pai: { id: number; organizationId: number | null; caminho: string }) => void
  onEditar: (id: number) => void
  onDetalhes: (id: number) => void
  onEnviar: (pasta: { id: number; caminho: string }) => void
  onExcluida: (parentId: number | null) => void
}

function Contador({ n, atalhos }: { n: number; atalhos: number }) {
  const docs = `${n} ${n === 1 ? 'documento' : 'documentos'}`
  return <Text size="xs" c="dimmed">{atalhos > 0 ? `${docs} · ${atalhos} ${atalhos === 1 ? 'atalho' : 'atalhos'}` : docs}</Text>
}

/** Subpasta na lista: abre com clique, e também arrasta e recebe soltura. */
function Subpasta({ s, onAbrir }: { s: KbFolderNode; onAbrir: (id: number) => void }) {
  const alvo = useAlvoDeArraste({ tipo: 'pasta', pasta: s })
  const origem = useOrigemDeArraste({ tipo: 'pasta', pasta: s })
  const arquivo = useAlvoDeArquivo(s)
  const estado = arquivo.estado !== 'neutro' ? arquivo.estado : alvo.estado
  return (
    <UnstyledButton
      ref={juntarRefs(alvo.setNodeRef, origem.setNodeRef)}
      {...origem.attributes}
      {...origem.listeners}
      {...arquivo.handlers}
      onClick={() => onAbrir(s.id)}
      py={4}
      px={4}
      style={{ ...estiloDoAlvo(estado), opacity: origem.isDragging ? 0.5 : undefined, borderRadius: 'var(--mantine-radius-sm)' }}
    >
      <Group gap="xs" wrap="nowrap">
        {s.liberada ? <IconFolder size={16} stroke={1.5} /> : <IconFolderOff size={16} stroke={1.5} />}
        <Text size="sm" c={s.liberada ? undefined : 'dimmed'}>{s.nome}</Text>
        {s.sensivel && <IconLock size={12} stroke={1.5} aria-label="sensível" />}
      </Group>
    </UnstyledButton>
  )
}

/** A pasta aberta inteira recebe arquivo do computador (D5); a subpasta recebe o dela. */
function PainelQueRecebeArquivos({ pasta, children }: { pasta: KbFolderNode; children: ReactNode }) {
  const arquivo = useAlvoDeArquivo(pasta)
  return (
    <Stack gap="md" {...arquivo.handlers} p={4} m={-4}
      style={{ ...estiloDoAlvo(arquivo.estado), borderRadius: 'var(--mantine-radius-sm)' }}>
      {children}
    </Stack>
  )
}

export function ConteudoDaPasta(p: Props) {
  const qc = useQueryClient()
  const { data: pasta, isLoading, error } = useQuery({
    queryKey: ['kb-folder', p.pastaId],
    queryFn: () => getKbFolder(p.pastaId),
  })

  const excluir = useMutation({
    mutationFn: () => deleteKbFolder(p.pastaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kb-folders'] })
      qc.invalidateQueries({ queryKey: ['kb-folder'] })
      qc.invalidateQueries({ queryKey: ['kb-folder-detalhes'] })
      notifications.show({ color: 'teal', title: 'Pasta excluída', message: pasta?.nome ?? '' })
      p.onExcluida(pasta?.parent_id ?? null)
    },
    onError: (e: unknown) =>
      notifications.show({ color: 'red', title: 'Não foi possível excluir', message: mensagemDeErro(e, 'Falha ao excluir a pasta.') }),
  })

  if (isLoading) return <Stack gap="sm"><Skeleton height={28} w={240} /><Skeleton height={80} /></Stack>
  if (error || !pasta) {
    return <Alert color="yellow" icon={<IconFolderOff size={16} />}>{mensagemDeErro(error, 'Pasta não encontrada.')}</Alert>
  }

  // Pasta da plataforma só é gerida por quem vê tudo (D16).
  const gereEsta = p.acesso.gere && (pasta.organization_id !== null || p.acesso.admin)
  const caminho = pasta.caminho ?? pasta.nome
  const docs = p.documentos
  // Só vêm com a pasta liberada (spec 2026-10-22, §4.3, item 5).
  const atalhos = pasta.atalhos ?? []

  const confirmarExclusao = () =>
    modals.openConfirmModal({
      title: 'Excluir pasta',
      children: <Text size="sm">Excluir a pasta <strong>{pasta.nome}</strong>? Só pasta vazia pode ser excluída.</Text>,
      labels: { confirm: 'Excluir', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: () => excluir.mutate(),
    })

  return (
    <PainelQueRecebeArquivos pasta={pasta}>
      <Stack gap={4}>
        {pasta.parent_id !== null && <Text size="xs" c="dimmed" data-testid="caminho">{caminho}</Text>}
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Group gap="xs">
            <Title order={3}>{pasta.nome}</Title>
            {pasta.sensivel && <Badge color="red" variant="light" leftSection={<IconLock size={11} />}>Sensível</Badge>}
          </Group>
          <Group gap="xs">
            {p.acesso.veDetalhes && (
              <Button size="xs" variant="default" leftSection={<IconInfoCircle size={14} />} onClick={() => p.onDetalhes(pasta.id)}>
                Detalhes
              </Button>
            )}
            {gereEsta && (
              <>
                <Button size="xs" variant="default" leftSection={<IconFolderPlus size={14} />}
                  onClick={() => p.onNovaSubpasta({ id: pasta.id, organizationId: pasta.organization_id, caminho })}>
                  Nova subpasta
                </Button>
                <Button size="xs" variant="default" leftSection={<IconEdit size={14} />} onClick={() => p.onEditar(pasta.id)}>
                  Editar
                </Button>
                <Button size="xs" variant="subtle" color="red" leftSection={<IconTrash size={14} />} loading={excluir.isPending} onClick={confirmarExclusao}>
                  Excluir
                </Button>
                <Button size="xs" leftSection={<IconUpload size={14} />} onClick={() => p.onEnviar({ id: pasta.id, caminho })}>
                  Enviar documento
                </Button>
              </>
            )}
          </Group>
        </Group>
        {pasta.liberada && pasta.descricao && <Text size="sm">{pasta.descricao}</Text>}
      </Stack>

      {pasta.subpastas.length > 0 && (
        <Stack gap={4} aria-label="Subpastas">
          {pasta.subpastas.map((s) => <Subpasta key={s.id} s={s} onAbrir={p.onAbrir} />)}
        </Stack>
      )}

      {!pasta.liberada && (
        <Alert color="gray" icon={<IconLock size={16} />} p="sm">
          <Text size="sm">{SEM_ACESSO_AO_CONTEUDO}</Text>
          {gereEsta && docs.length > 0 && (
            <Text size="xs" c="dimmed" mt={4}>
              Você gerencia esta pasta: vê os documentos para mover, reindexar e excluir, mas não abre o conteúdo.
            </Text>
          )}
        </Alert>
      )}

      {(pasta.liberada || (gereEsta && docs.length > 0)) && (
        <Stack gap="xs">
          <Contador n={docs.length} atalhos={atalhos.length} />
          {docs.length > 0 || atalhos.length > 0 ? (
            <DocumentosDaPasta
              documentos={docs}
              atalhos={atalhos}
              onAbrirPasta={p.onAbrir}
              podeLer={pasta.liberada}
              gere={gereEsta}
              pastaId={pasta.id}
              destinos={p.pastasDaOrg}
            />
          ) : (
            <Text size="sm" c="dimmed">Nenhum documento nesta pasta.</Text>
          )}
        </Stack>
      )}
    </PainelQueRecebeArquivos>
  )
}
